import { FormInstance } from 'antd'
import dayjs from 'dayjs'
import { useEffect } from 'react'

import { useClassifierServiceGetCommonClassifierElementListEndpoint } from 'src/api/queries'
import {
  ClassifierCategory,
  ClassifierType,
  GetPersonByIdResponse,
  PersonCaseStatus
} from 'src/api/requests'
import { useLatestConsultationDiagnoses } from 'src/features/persons/hooks/useLatestConsultationDiagnoses'
import { useClassifierId } from 'src/hooks'

type ConsultationDiagnosis = {
  id?: string
  code?: string | null
  translation?: { ro?: string | null; ru?: string | null } | null
}

const toFormDiagnoses = (items?: ConsultationDiagnosis[] | null) =>
  (items ?? [])
    .filter((item) => !!item.id)
    .map((item) => ({
      id: item.id,
      code: item.code,
      name: { ro: item.translation?.ro, ru: item.translation?.ru }
    }))

/** On a new 058/e or SIDA form, fills from the person's consultations:
 *  - primary diagnosis: the newest consultation with a diagnosis — its
 *    diagnoses (final; preliminary on older consultations without a final
 *    one), its date as the diagnosis date and its disease onset;
 *  - final diagnosis: the newest consultation with final diagnoses, its date
 *    and its institution (name + IDNO) — or, without such a consultation,
 *    `fallbackInstitution` (the current user's institution);
 *  - classification level: from the person's case status (A1/A2/A3 positive);
 *    a patient (SIME/TARV code) without one is a confirmed case.
 *  A field the user already filled is left as it is. */
export const useLatestConsultationDiagnosesPrefill = (
  form: FormInstance,
  personId?: string,
  enabled = true,
  person?: GetPersonByIdResponse,
  fallbackInstitution?: { name?: string | null; code?: string | null }
) => {
  const { primary, primaryDiagnostics, final, isLoaded } =
    useLatestConsultationDiagnoses(personId, enabled)

  const classificationClassifierId = useClassifierId(
    ClassifierType.EPIDEMIOLOGY_CASE_CLASSIFICATION,
    ClassifierCategory.TREATMENT
  )

  const { data: classificationElements } =
    useClassifierServiceGetCommonClassifierElementListEndpoint(
      { id: String(classificationClassifierId) },
      [classificationClassifierId],
      { enabled: enabled && !!classificationClassifierId }
    )

  const fillIfEmpty = (path: string[], value: unknown) => {
    const current = form.getFieldValue(path)
    const isEmpty =
      current === undefined ||
      current === null ||
      current === '' ||
      (Array.isArray(current) && current.length === 0)

    if (value !== undefined && value !== null && isEmpty) {
      form.setFieldValue(path, value)
    }
  }

  useEffect(() => {
    if (!enabled || !isLoaded) return

    if (primary) {
      fillIfEmpty(
        ['form', 'primaryDiagnostic', 'diagnoses'],
        toFormDiagnoses(primaryDiagnostics)
      )
      if (primary.consultationDate) {
        fillIfEmpty(
          ['form', 'primaryDiagnostic', 'diagnosisDate'],
          dayjs(primary.consultationDate)
        )
      }
      if (
        primary.dateOfDiseaseOnset &&
        dayjs(primary.dateOfDiseaseOnset).year() > 1900
      ) {
        fillIfEmpty(
          ['form', 'primaryDiagnostic', 'dateOfDiseaseOnset'],
          dayjs(primary.dateOfDiseaseOnset)
        )
      }
    }

    if (final) {
      fillIfEmpty(
        ['form', 'finalDiagnostic', 'diagnoses'],
        toFormDiagnoses(final.finalDiagnostics)
      )
      if (final.consultationDate) {
        fillIfEmpty(
          ['form', 'finalDiagnostic', 'diagnosisDate'],
          dayjs(final.consultationDate)
        )
      }
    }

    const institution = final?.institution ?? fallbackInstitution
    if (institution) {
      fillIfEmpty(
        ['form', 'finalDiagnostic', 'institution', 'title'],
        institution.name
      )
      fillIfEmpty(
        ['form', 'finalDiagnostic', 'institution', 'code'],
        institution.code
      )
    }
  }, [
    primary,
    final,
    isLoaded,
    enabled,
    fallbackInstitution?.name,
    fallbackInstitution?.code
  ])

  useEffect(() => {
    if (!enabled || !person || !classificationElements?.data) return

    const caseStatus =
      person.caseStatus ??
      (person.simeCode || person.tarvCode
        ? PersonCaseStatus.CONFIRMED_CASE
        : undefined)
    if (!caseStatus) return

    const element = classificationElements.data.find(
      (item) => item.originalValue === caseStatus
    )
    if (!element) return

    fillIfEmpty(['form', 'primaryDiagnostic', 'classificationLevel'], {
      id: element.id,
      name: element.translation
    })
  }, [person, classificationElements, enabled])
}
